import { probeExecutionSchema } from '../agent/target'
import { z } from 'zod'
import { environmentReferenceSchema, httpUrlSchema, idSchema } from './common'
import { contentVisibilitySchema } from './contentVisibility'
import { incidentSeveritySchema } from './incident'

export const monitoringServiceStatusSchema = z.enum([
  'unknown',
  'operational',
  'degraded',
  'down',
  'maintenance',
])
export const monitoringProbeStatusSchema = z.enum(['unknown', 'operational', 'degraded', 'down'])
export const monitoringProbeTypeSchema = z.enum(['http', 'tcp', 'udp', 'icmp'])
export const monitoringResultStatusSchema = z.enum([
  'success',
  'slow',
  'failure',
  'executor_unavailable',
])
export const monitoringEventTypeSchema = z.enum([
  'outage',
  'degradation',
  'slow',
  'tls_warning',
  'tls_critical',
])
export const monitoringAudienceModeSchema = z.enum(['everyone', 'restricted'])

const hostSchema = z
  .string()
  .trim()
  .min(1)
  .max(253)
  .refine((value) => /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/i.test(value), 'hôte invalide')
const portSchema = z.number().int().min(1).max(65535)
const secretValueSchema = z.union([z.string().max(1000), environmentReferenceSchema])
const headerSchema = z
  .record(z.string().trim().min(1).max(100), secretValueSchema)
  .superRefine((headers, context) => {
    for (const [name, value] of Object.entries(headers)) {
      const compact = name.toLowerCase().replaceAll(/[-_]/g, '')
      if (
        typeof value === 'string' &&
        /authorization|cookie|token|secret|apikey|password|credential/.test(compact)
      )
        context.addIssue({
          code: 'custom',
          path: [name],
          message: 'un en-tête sensible doit utiliser une référence env',
        })
    }
  })
  .default({})
const expectedStatusRangeSchema = z
  .object({ from: z.number().int().min(100).max(599), to: z.number().int().min(100).max(599) })
  .strict()
  .refine((value) => value.from <= value.to, {
    message: 'la borne basse doit précéder la borne haute',
  })
const expectedStatusSchema = z
  .array(z.union([z.number().int().min(100).max(599), expectedStatusRangeSchema]))
  .min(1, 'au moins un code HTTP attendu est requis')
  .max(30)
  .default([{ from: 200, to: 399 }])

export const httpProbeConfigSchema = z
  .object({
    url: httpUrlSchema.refine((value) => {
      const parsed = new URL(value)
      return (
        !parsed.username &&
        !parsed.password &&
        ![...parsed.searchParams.keys()].some((key) =>
          /token|key|auth|password|secret|credential/i.test(key),
        )
      )
    }, 'Les identifiants URL doivent provenir de variables d’environnement'),
    method: z.enum(['GET', 'HEAD', 'POST', 'PUT', 'PATCH', 'DELETE']).default('GET'),
    headers: headerSchema,
    basicAuth: z
      .object({ username: secretValueSchema, password: environmentReferenceSchema })
      .strict()
      .optional(),
    bearerToken: environmentReferenceSchema.optional(),
    body: environmentReferenceSchema.optional(),
    followRedirects: z.boolean().default(true),
    allowCrossOriginRedirects: z.boolean().default(true),
    redirectLimit: z.number().int().min(0).max(10).default(5),
    expectedStatus: expectedStatusSchema,
    assertion: z
      .object({
        contains: z.string().max(500).optional(),
        notContains: z.string().max(500).optional(),
        regex: z.string().max(500).optional(),
      })
      .strict()
      .optional(),
    hostHeader: z.string().trim().min(1).max(253).optional(),
    servername: hostSchema.optional(),
    rejectUnauthorized: z.boolean().default(true),
  })
  .strict()
export const tcpProbeConfigSchema = z.object({ host: hostSchema, port: portSchema }).strict()
export const udpProbeConfigSchema = z
  .object({ host: hostSchema, port: portSchema, payload: z.string().max(1024).default('') })
  .strict()
export const icmpProbeConfigSchema = z.object({ destination: hostSchema }).strict()
export const monitoringProbeConfigSchema = z.union([
  httpProbeConfigSchema,
  tcpProbeConfigSchema,
  udpProbeConfigSchema,
  icmpProbeConfigSchema,
])

const probeCore = {
  execution: probeExecutionSchema.optional(),
  id: idSchema,
  name: z.string().trim().min(1).max(100),
  type: monitoringProbeTypeSchema,
  applicationIds: z
    .array(idSchema)
    .max(100)
    .default([])
    .transform((ids) => [...new Set(ids)]),
  enabled: z.boolean().default(true),
  required: z.boolean().default(true),
  intervalSeconds: z.number().int().min(5).max(86400).default(60),
  timeoutMs: z.number().int().min(100).max(120000).default(5000),
  failureThreshold: z.number().int().min(1).max(20).default(3),
  recoveryThreshold: z.number().int().min(1).max(20).default(2),
  slowThresholdMs: z.number().int().min(1).max(120000).nullable().default(null),
  autoIncidentEnabled: z.boolean().default(false),
  autoIncidentSeverity: incidentSeveritySchema.default('major'),
  autoIncidentBlockApplication: z.boolean().default(false),
  tlsIncidentEnabled: z.boolean().default(false),
  tlsWarningDays: z.number().int().min(1).max(3650).default(30),
  tlsCriticalDays: z.number().int().min(0).max(3650).default(7),
}

export const monitoringProbeInputSchema = z
  .object({ ...probeCore, config: monitoringProbeConfigSchema })
  .strict()
  .superRefine((value, context) => {
    const matches =
      value.type === 'http'
        ? httpProbeConfigSchema.safeParse(value.config).success
        : value.type === 'tcp'
          ? tcpProbeConfigSchema.safeParse(value.config).success
          : value.type === 'udp'
            ? udpProbeConfigSchema.safeParse(value.config).success
            : icmpProbeConfigSchema.safeParse(value.config).success
    if (!matches)
      context.addIssue({
        code: 'custom',
        path: ['config'],
        message: `configuration incompatible avec une sonde ${value.type}`,
      })
    if (value.tlsCriticalDays >= value.tlsWarningDays)
      context.addIssue({
        code: 'custom',
        path: ['tlsCriticalDays'],
        message: 'le seuil critique doit être inférieur au seuil d’avertissement',
      })
  })

export const monitoringServiceInputSchema = z
  .object({
    id: idSchema,
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().max(1000).default(''),
    enabled: z.boolean().default(true),
    applicationId: idSchema.nullable().default(null),
    maintenance: z.boolean().default(false),
    resultRetentionDays: z.number().int().min(0).max(3650).nullable().default(null),
    eventRetentionDays: z.number().int().min(0).max(3650).nullable().default(null),
    rollupRetentionDays: z.number().int().min(0).max(7300).nullable().default(null),
    probes: z.array(monitoringProbeInputSchema).max(50).default([]),
  })
  .strict()
  .superRefine((value, context) => {
    const ids = new Set<string>()
    value.probes.forEach((probe, index) => {
      if (ids.has(probe.id))
        context.addIssue({
          code: 'custom',
          path: ['probes', index, 'id'],
          message: 'identifiant de sonde dupliqué',
        })
      ids.add(probe.id)
    })
  })

// YAML keeps the concise, documented probe syntax; the persisted/API shape remains explicit.
const monitoringYamlProbeSchema = z.preprocess((input) => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return input
  const source = input as Record<string, unknown>
  const {
    url,
    method,
    headers,
    basicAuth,
    bearerToken,
    body,
    followRedirects,
    allowCrossOriginRedirects,
    redirectLimit,
    expectedStatus,
    assertion,
    hostHeader,
    servername,
    rejectUnauthorized,
    host,
    port,
    payload,
    destination,
    ...probe
  } = source
  const flatConfig = Object.fromEntries(
    Object.entries({
      url,
      method,
      headers,
      basicAuth,
      bearerToken,
      body,
      followRedirects,
      allowCrossOriginRedirects,
      redirectLimit,
      expectedStatus,
      assertion,
      hostHeader,
      servername,
      rejectUnauthorized,
      host,
      port,
      payload,
      destination,
    }).filter(([, value]) => value !== undefined),
  )
  return { ...probe, name: probe.name ?? probe.id, config: source.config ?? flatConfig }
}, monitoringProbeInputSchema)
const monitoringYamlServiceSchema = z
  .object({
    id: idSchema,
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().max(1000).default(''),
    enabled: z.boolean().default(true),
    applicationId: idSchema.nullable().default(null),
    maintenance: z.boolean().default(false),
    resultRetentionDays: z.number().int().min(0).max(3650).nullable().default(null),
    eventRetentionDays: z.number().int().min(0).max(3650).nullable().default(null),
    rollupRetentionDays: z.number().int().min(0).max(7300).nullable().default(null),
    probes: z.array(monitoringYamlProbeSchema).max(50).default([]),
  })
  .strict()
  .superRefine((value, context) => {
    const ids = new Set<string>()
    value.probes.forEach((probe, index) => {
      if (ids.has(probe.id))
        context.addIssue({
          code: 'custom',
          path: ['probes', index, 'id'],
          message: 'identifiant de sonde dupliqué',
        })
      ids.add(probe.id)
    })
  })
export const monitoringYamlSchema = z
  .object({ services: z.array(monitoringYamlServiceSchema).max(500).default([]) })
  .strict()
export const monitoringVisibilitySchema = z
  .object({
    mode: monitoringAudienceModeSchema,
    allow: z
      .object({
        userIds: z.array(z.string().uuid()).max(500).default([]),
        groupIds: z.array(z.string().uuid()).max(500).default([]),
        roleIds: z.array(z.string().uuid()).max(500).default([]),
      })
      .strict()
      .default({ userIds: [], groupIds: [], roleIds: [] }),
    deny: z
      .object({
        userIds: z.array(z.string().uuid()).max(500).default([]),
        groupIds: z.array(z.string().uuid()).max(500).default([]),
        roleIds: z.array(z.string().uuid()).max(500).default([]),
      })
      .strict()
      .default({ userIds: [], groupIds: [], roleIds: [] }),
  })
  .strict()
export const monitoringHistoryQuerySchema = z
  .object({
    from: z.coerce.number().int().nonnegative().optional(),
    to: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().min(1).max(1000).default(200),
  })
  .strict()
export const monitoringAnalyticsQuerySchema = z
  .object({ from: z.coerce.number().int().nonnegative(), to: z.coerce.number().int().positive() })
  .strict()
  .refine((value) => value.from < value.to, 'période invalide')
export const statusPageModuleSchema = z.enum([
  'services',
  'uptime',
  'latency',
  'incidents',
  'events',
  'tls',
])
export const DEFAULT_STATUS_PAGE_LAYOUT = {
  modules: ['services', 'uptime', 'latency', 'incidents', 'events', 'tls'] satisfies Array<
    z.infer<typeof statusPageModuleSchema>
  >,
  showProbes: true,
}
export const statusPageLayoutSchema = z
  .object({
    modules: z
      .array(statusPageModuleSchema)
      .min(1)
      .max(6)
      .refine((modules) => new Set(modules).size === modules.length, 'modules dupliqués')
      .default([...DEFAULT_STATUS_PAGE_LAYOUT.modules]),
    showProbes: z.boolean().default(true),
  })
  .strict()
export const statusPageInputSchema = z
  .object({
    id: idSchema,
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().max(1000).default(''),
    enabled: z.boolean().default(true),
    serviceIds: z.array(idSchema).max(100).default([]),
    layout: statusPageLayoutSchema.default(DEFAULT_STATUS_PAGE_LAYOUT),
    visibility: contentVisibilitySchema.optional(),
  })
  .strict()

export type StatusPageLayout = z.infer<typeof statusPageLayoutSchema>
export type StatusPageModule = z.infer<typeof statusPageModuleSchema>

export type MonitoringProbeInput = z.input<typeof monitoringProbeInputSchema>
export type MonitoringServiceInput = z.input<typeof monitoringServiceInputSchema>
export type MonitoringProbeType = z.infer<typeof monitoringProbeTypeSchema>
export type MonitoringProbeStatus = z.infer<typeof monitoringProbeStatusSchema>
export type MonitoringServiceStatus = z.infer<typeof monitoringServiceStatusSchema>
export type MonitoringResultStatus = z.infer<typeof monitoringResultStatusSchema>
export type MonitoringEventType = z.infer<typeof monitoringEventTypeSchema>
